import { useState } from 'react';

// Self-hosted form gateway (matou_forms role in matou-distributed-servers):
// validates + rate-limits, then emails contact@matou.nz via Stalwart.
const CONTACT_ENDPOINT = 'https://forms.matou.nz/api/contact/matou';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    message: '',
    website: '', // honeypot — hidden field real users never fill
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'message' && value.length > 500) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const message = formData.organization
        ? `Organisation: ${formData.organization}\n\n${formData.message}`
        : formData.message;

      const response = await fetch(CONTACT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message,
          website: formData.website,
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', organization: '', message: '', website: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-16 md:py-24 bg-matou-teal-dark">
      <div className="max-w-content mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className="bg-matou-teal-deep rounded-t-md px-5 py-3">
          <h2 className="font-heading text-white text-sm font-bold uppercase tracking-widest">
            Let's Connect
          </h2>
        </div>

        <div className="border border-t-0 border-black/10 rounded-b-md bg-matou-bg p-6 md:p-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Contact Form */}
            <div className="bg-matou-surface rounded-lg border border-black/10 p-6 md:p-8">
              <h3 className="font-heading text-lg font-bold text-matou-text mb-6">
                Send us a message
              </h3>

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Honeypot: hidden from real users; the gateway silently drops
                    any submission where this field is filled in */}
                <div className="absolute -left-[9999px]" aria-hidden="true">
                  <label htmlFor="website">Website</label>
                  <input
                    type="text"
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label htmlFor="name" className="block font-mono text-xs font-semibold text-matou-text uppercase tracking-wider mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-black/10 rounded-md font-mono text-sm text-matou-text bg-matou-bg focus:outline-none focus:ring-2 focus:ring-matou-teal-medium focus:border-matou-teal-medium transition-colors"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block font-mono text-xs font-semibold text-matou-text uppercase tracking-wider mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-black/10 rounded-md font-mono text-sm text-matou-text bg-matou-bg focus:outline-none focus:ring-2 focus:ring-matou-teal-medium focus:border-matou-teal-medium transition-colors"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="organization" className="block font-mono text-xs font-semibold text-matou-text uppercase tracking-wider mb-2">
                    Organisation
                  </label>
                  <input
                    type="text"
                    id="organization"
                    name="organization"
                    value={formData.organization}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-black/10 rounded-md font-mono text-sm text-matou-text bg-matou-bg focus:outline-none focus:ring-2 focus:ring-matou-teal-medium focus:border-matou-teal-medium transition-colors"
                    placeholder="Your organisation or community"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block font-mono text-xs font-semibold text-matou-text uppercase tracking-wider mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-black/10 rounded-md font-mono text-sm text-matou-text bg-matou-bg focus:outline-none focus:ring-2 focus:ring-matou-teal-medium focus:border-matou-teal-medium transition-colors resize-none"
                    placeholder="Tell us about your project or how we can help..."
                  />
                  <p className="font-mono text-xs text-matou-text-muted text-right mt-1">
                    {formData.message.length}/500 characters
                  </p>
                </div>

                {submitStatus === 'success' && (
                  <div className="bg-growth-fill border border-growth-stroke/30 rounded-md p-4">
                    <p className="font-mono text-sm text-growth-stroke">
                      Thank you! Your message has been sent successfully.
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-accessibility-fill border border-accessibility-stroke/30 rounded-md p-4">
                    <p className="font-mono text-sm text-accessibility-stroke">
                      Sorry, there was an error. Please try again or email us directly.
                    </p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full font-mono text-sm font-semibold bg-matou-teal-medium text-white px-6 py-3 rounded-md hover:bg-matou-teal-deep disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>

            {/* Get in Touch */}
            <div className="space-y-6">
              <div className="bg-matou-teal-dark rounded-lg p-6 md:p-8">
                <h3 className="font-heading text-lg font-bold text-white mb-6">
                  Get in Touch
                </h3>

                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-matou-teal-soft text-sm">@</span>
                    </div>
                    <div>
                      <p className="font-mono text-xs font-semibold text-white/60 uppercase tracking-wider mb-1">Email</p>
                      <a
                        href="mailto:contact@matou.nz"
                        className="font-mono text-sm text-matou-teal-soft hover:text-white transition-colors"
                      >
                        contact@matou.nz
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-matou-teal-soft text-sm">~</span>
                    </div>
                    <div>
                      <p className="font-mono text-xs font-semibold text-white/60 uppercase tracking-wider mb-1">Response Time</p>
                      <p className="font-mono text-sm text-matou-teal-soft">
                        Within 24 – 48 hours
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Follow Our Journey */}
              <div className="bg-matou-surface rounded-lg border border-black/10 p-6 md:p-8">
                <h3 className="font-heading text-lg font-bold text-matou-text mb-4">
                  Follow Our Journey
                </h3>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="https://x.com/MatouCollective"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 bg-matou-bg hover:bg-matou-teal-soft/20 px-4 py-3 rounded-md transition-colors flex-1"
                  >
                    <svg className="w-5 h-5 text-matou-text-muted flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                    <span className="font-mono text-sm text-matou-text">Twitter</span>
                  </a>
                  <a
                    href="https://www.linkedin.com/company/m%C4%81tou-collective/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 bg-matou-bg hover:bg-matou-teal-soft/20 px-4 py-3 rounded-md transition-colors flex-1"
                  >
                    <svg className="w-5 h-5 text-matou-text-muted flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                    <span className="font-mono text-sm text-matou-text">LinkedIn</span>
                  </a>
                  <a
                    href="https://gitlab.com/matou-collective"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 bg-matou-bg hover:bg-matou-teal-soft/20 px-4 py-3 rounded-md transition-colors flex-1"
                  >
                    <svg className="w-5 h-5 text-matou-text-muted flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M23.955 13.587l-1.342-4.135-2.664-8.189a.455.455 0 00-.867 0L16.418 9.45H7.582L4.918 1.263a.455.455 0 00-.867 0L1.386 9.45.044 13.587a.924.924 0 00.331 1.023L12 23.054l11.625-8.443a.92.92 0 00.33-1.024" />
                    </svg>
                    <span className="font-mono text-sm text-matou-text">GitLab</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
